import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { clientService } from '../services/ClientService';
import { userService } from '../services/UserService';
import { Client, ClientFormData } from '../models/Client';
import { Users, UserPlus, MapPin, Phone, Mail, Edit2, Trash2, Calendar, ClipboardList } from 'lucide-react';
import { AddClientModal } from '../components/modals/AddClientModal';
import { EditClientModal } from '../components/modals/EditClientModal';
import { ConfirmModal } from '../components/modals/ConfirmModal';
import { AddAppointmentModal } from '../components/modals/AddAppointmentModal';
import { appointmentService } from '../services/AppointmentService';
import { AppointmentFormData } from '../models/Appointment';

export const Clients: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { company } = useCompany();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<{ id: string; name: string } | null>(null);
  const [rbtUsers, setRbtUsers] = useState<Array<{ id: string; name: string }>>([]);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedClientForAppointment, setSelectedClientForAppointment] = useState<Client | null>(null);

  useEffect(() => {
    loadClients();
    loadRBTs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const loadClients = async () => {
    if (!company) {
      setLoading(false);
      return;
    }

    try {
      const companyClients = await clientService.getClientsByCompany(company.id);
      setClients(companyClients);
      setError(null);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const loadRBTs = async () => {
    if (!company) return;

    try {
      const users = await userService.getUsersByCompany(company.id);
      const rbts = users
        .filter(user => user.role === 'rbt')
        .map(user => ({ id: user.id, name: user.name }));
      setRbtUsers(rbts);
    } catch (err) {
      console.error('Error loading RBTs:', err);
    }
  };

  const handleAddClient = async (clientData: ClientFormData) => {
    if (!company || !currentUser) {
      throw new Error('No company or user selected');
    }

    await clientService.createClient(company.id, currentUser.id, clientData);
    setSuccessMessage(`Client ${clientData.firstName} ${clientData.lastName} has been added successfully!`);

    // Reload clients
    await loadClients();

    // Clear success message after 3 seconds
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleEditClient = async (clientId: string, clientData: Partial<ClientFormData>) => {
    await clientService.updateClient(clientId, clientData);
    setSuccessMessage(`Client has been updated successfully!`);
    await loadClients();
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleDeleteClick = (clientId: string, clientName: string) => {
    setClientToDelete({ id: clientId, name: clientName });
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;

    try {
      await clientService.deleteClient(clientToDelete.id);
      setSuccessMessage(`Client ${clientToDelete.name} has been deleted successfully!`);
      await loadClients();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Failed to delete client');
      setTimeout(() => setError(null), 3000);
    } finally {
      setClientToDelete(null);
    }
  };

  const handleStatusChange = async (clientId: string, newStatus: 'active' | 'inactive' | 'discharged') => {
    try {
      await clientService.setClientStatus(clientId, newStatus);
      setSuccessMessage(`Client status updated to ${newStatus}`);
      await loadClients();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating client status:', err);
      setError('Failed to update client status');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleAssignRBT = async (clientId: string, rbtId: string) => {
    if (rbtId === '') return; // No selection

    try {
      await clientService.assignRbtToClient(clientId, rbtId);
      setSuccessMessage('RBT assigned successfully');
      await loadClients();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error assigning RBT:', err);
      setError('Failed to assign RBT');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleUnassignRBT = async (clientId: string, rbtId: string) => {
    try {
      await clientService.removeRbtFromClient(clientId, rbtId);
      setSuccessMessage('RBT unassigned successfully');
      await loadClients();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error unassigning RBT:', err);
      setError('Failed to unassign RBT');
      setTimeout(() => setError(null), 3000);
    }
  };

  const getRBTName = (rbtId: string) => {
    const rbt = rbtUsers.find(r => r.id === rbtId);
    return rbt ? rbt.name : 'Unknown';
  };

  const handleCreateAppointment = async (appointmentData: AppointmentFormData) => {
    if (!company || !currentUser || !selectedClientForAppointment) {
      throw new Error('Missing required data');
    }

    try {
      // Use the RBT explicitly chosen in the form, else the client's assigned RBT.
      const rbtId = appointmentData.rbtId || selectedClientForAppointment.assignedRbtIds?.[0];
      if (!rbtId) {
        throw new Error('This client has no assigned RBT. Please select an RBT.');
      }

      // Get the RBT's supervisor
      const rbtUser = await userService.getUser(rbtId);
      if (!rbtUser) {
        throw new Error('RBT user not found');
      }

      // Determine supervisor: use RBT's supervisor, or current user if they're a supervisor/admin
      let supervisorId = rbtUser.supervisorId;
      if (!supervisorId) {
        if (currentUser.role === 'supervisor') {
          supervisorId = currentUser.id;
        } else if (currentUser.role === 'admin') {
          // Admin booking - find any supervisor (we'll use OG's supervisor account)
          const supervisors = await userService.getUsersByCompany(company.id);
          const anySupervisor = supervisors.find(u => u.role === 'supervisor');
          supervisorId = anySupervisor?.id || currentUser.id;
        } else {
          throw new Error('No supervisor assigned to the RBT. Please contact your administrator.');
        }
      }

      // Create the appointment
      await appointmentService.createAppointment(
        company.id,
        rbtId,
        supervisorId,
        selectedClientForAppointment,
        currentUser.id,
        appointmentData
      );

      setSuccessMessage('Appointment scheduled successfully!');
      setShowAppointmentModal(false);
      setSelectedClientForAppointment(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error creating appointment:', err);
      throw err;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'badge-success';
      case 'inactive':
        return 'badge-warning';
      case 'discharged':
        return 'badge-error';
      default:
        return '';
    }
  };

  const isRBT = currentUser?.role === 'rbt';
  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'supervisor';

  if (!canEdit && !isRBT) {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)' }}>
        <div className="alert alert-error">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-block',
              width: '48px',
              height: '48px',
              border: '4px solid var(--color-gray-200)',
              borderTop: '4px solid var(--color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ marginTop: 'var(--spacing-4)', color: 'var(--color-gray-600)' }}>
            Loading clients...
          </p>
        </div>
    );
  }

  return (
    <>
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', paddingBottom: 'var(--spacing-8)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-8)' }}>
          <div>
            <h1 style={{ marginBottom: 'var(--spacing-2)' }}>Clients {isRBT && <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', fontWeight: 'normal' }}>(View Only)</span>}</h1>
            <p style={{ color: 'var(--color-gray-600)' }}>
              {company?.name} - {clients.length} {clients.length === 1 ? 'client' : 'clients'}
            </p>
          </div>
          {canEdit && (
            <button
              onClick={() => setShowAddClientModal(true)}
              className="btn btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                backgroundColor: 'var(--color-primary-dark)',
                padding: 'var(--spacing-3) var(--spacing-6)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
            >
              <UserPlus size={20} />
              Add Client
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-6)' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success" style={{ marginBottom: 'var(--spacing-6)' }}>
            {successMessage}
          </div>
        )}

        {/* Client Statistics */}
        <div className="grid grid-cols-3" style={{ marginBottom: 'var(--spacing-8)', gap: 'var(--spacing-6)' }}>
          <div className="card">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-success)',
                  color: 'var(--color-white)',
                  marginBottom: 'var(--spacing-3)',
                }}
              >
                <Users size={24} />
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>Active Clients</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>
                {clients.filter((c) => c.status === 'active').length}
              </p>
            </div>
          </div>

          <div className="card">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-warning)',
                  color: 'var(--color-white)',
                  marginBottom: 'var(--spacing-3)',
                }}
              >
                <Users size={24} />
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>Inactive Clients</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>
                {clients.filter((c) => c.status === 'inactive').length}
              </p>
            </div>
          </div>

          <div className="card">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '48px',
                  height: '48px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-gray-400)',
                  color: 'var(--color-white)',
                  marginBottom: 'var(--spacing-3)',
                }}
              >
                <Users size={24} />
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>Discharged</p>
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>
                {clients.filter((c) => c.status === 'discharged').length}
              </p>
            </div>
          </div>
        </div>

        {/* Clients List */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">All Clients</h2>
          </div>

          {clients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-gray-500)' }}>
              <Users size={48} style={{ margin: '0 auto var(--spacing-4)' }} />
              <p>No clients found</p>
              <p style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--spacing-2)' }}>
                Click "Add Client" to add your first client
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 'var(--spacing-4)' }}>
              {clients.map((client) => (
                <div
                  key={client.id}
                  style={{
                    padding: 'var(--spacing-4)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: 'var(--radius-lg)',
                    backgroundColor: 'var(--color-gray-50)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--spacing-3)' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                        {client.firstName} {client.lastName}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                        <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', fontWeight: 'var(--font-medium)' }}>
                          Status:
                        </label>
                        {canEdit ? (
                          <select
                            value={client.status}
                            onChange={(e) => handleStatusChange(client.id, e.target.value as 'active' | 'inactive' | 'discharged')}
                            className="form-input"
                            style={{
                              fontSize: 'var(--text-sm)',
                              padding: 'var(--spacing-1) var(--spacing-2)',
                              width: 'auto',
                              minWidth: '120px'
                            }}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="discharged">Discharged</option>
                          </select>
                        ) : (
                          <span style={{ fontSize: 'var(--text-sm)', padding: 'var(--spacing-1) var(--spacing-2)' }} className={`badge ${getStatusBadgeClass(client.status)}`}>
                            {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                          </span>
                        )}
                      </div>

                      {/* Assigned RBTs */}
                      <div style={{ marginBottom: 'var(--spacing-2)' }}>
                        <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', fontWeight: 'var(--font-medium)', display: 'block', marginBottom: 'var(--spacing-1)' }}>
                          Assigned RBTs:
                        </label>
                        {client.assignedRbtIds && client.assignedRbtIds.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-1)', marginBottom: 'var(--spacing-1)' }}>
                            {client.assignedRbtIds.map(rbtId => (
                              <span
                                key={rbtId}
                                className="badge badge-secondary"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 'var(--spacing-1)',
                                  paddingRight: canEdit ? 'var(--spacing-1)' : 'var(--spacing-2)'
                                }}
                              >
                                {getRBTName(rbtId)}
                                {canEdit && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUnassignRBT(client.id, rbtId);
                                    }}
                                    style={{
                                      background: 'var(--color-error)',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: 'white',
                                      padding: '0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '50%',
                                      fontSize: '18px',
                                      fontWeight: 'bold',
                                      width: '20px',
                                      height: '20px',
                                      transition: 'background 0.2s',
                                      flexShrink: 0
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'var(--color-error)'}
                                    title="Remove RBT"
                                    type="button"
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', marginBottom: 'var(--spacing-1)' }}>
                            No RBTs assigned
                          </p>
                        )}
                        {canEdit && (
                          <select
                            onChange={(e) => {
                              handleAssignRBT(client.id, e.target.value);
                              e.target.value = ''; // Reset selection
                            }}
                            className="form-input"
                            style={{
                              fontSize: 'var(--text-sm)',
                              padding: 'var(--spacing-1) var(--spacing-2)',
                              width: '100%'
                            }}
                            defaultValue=""
                          >
                            <option value="">+ Assign RBT</option>
                            {rbtUsers.map(rbt => (
                              <option
                                key={rbt.id}
                                value={rbt.id}
                                disabled={client.assignedRbtIds?.includes(rbt.id)}
                              >
                                {rbt.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                    {canEdit && (
                      <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                        <button
                          onClick={() => {
                            setSelectedClientForAppointment(client);
                            setShowAppointmentModal(true);
                          }}
                          className="btn btn-primary"
                          style={{
                            padding: 'var(--spacing-2)',
                            minWidth: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-1)',
                            backgroundColor: 'var(--color-primary-dark)'
                          }}
                          title="Schedule appointment"
                        >
                          <Calendar size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedClient(client);
                            setShowEditClientModal(true);
                          }}
                          className="btn btn-secondary"
                          style={{
                            padding: 'var(--spacing-2)',
                            minWidth: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-1)'
                          }}
                          title="Edit client"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => navigate(`/clients/${client.id}/clinical`)}
                          className="btn btn-secondary"
                          style={{
                            padding: 'var(--spacing-2)',
                            minWidth: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-1)',
                          }}
                          title="Clinical record (assessments & treatment plan)"
                        >
                          <ClipboardList size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(client.id, `${client.firstName} ${client.lastName}`)}
                          className="btn btn-secondary"
                          style={{
                            padding: 'var(--spacing-2)',
                            minWidth: 'auto',
                            color: 'var(--color-error)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-1)'
                          }}
                          title="Delete client"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                    {client.guardianName && (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                          <Users size={14} />
                          <span>Guardian: {client.guardianName}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                          <Phone size={14} />
                          <span>{client.guardianPhone}</span>
                        </div>
                        {client.guardianEmail && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                            <Mail size={14} />
                            <span>{client.guardianEmail}</span>
                          </div>
                        )}
                      </>
                    )}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)' }}>
                      <MapPin size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                      <span>
                        {client.address}, {client.city}, {client.state} {client.zipCode}
                      </span>
                    </div>
                  </div>

                  {client.notes && (
                    <div
                      style={{
                        marginTop: 'var(--spacing-3)',
                        paddingTop: 'var(--spacing-3)',
                        borderTop: '1px solid var(--color-gray-200)',
                      }}
                    >
                      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', fontStyle: 'italic' }}>
                        {client.notes}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <AddClientModal
        isOpen={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        onSubmit={handleAddClient}
      />

      <EditClientModal
        isOpen={showEditClientModal}
        onClose={() => {
          setShowEditClientModal(false);
          setSelectedClient(null);
        }}
        onSubmit={handleEditClient}
        client={selectedClient}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setClientToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Client"
        message={`Are you sure you want to delete ${clientToDelete?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <AddAppointmentModal
        isOpen={showAppointmentModal}
        onClose={() => {
          setShowAppointmentModal(false);
          setSelectedClientForAppointment(null);
        }}
        onSubmit={handleCreateAppointment}
        clients={clients}
        rbts={rbtUsers}
        preselectedClientId={selectedClientForAppointment?.id}
      />
    </>
  );
};
